import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import { fmtFCFA, esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { canMutateComptaRecords, canMutateRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { onExclusive } from '@presentation/shell/viewDom';
import {
  bindCrudTable,
  crudActions,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
  type FieldDef,
} from '@presentation/shell/localCrud';

export interface NoteFraisLocal {
  id: string;
  salarie_id: string;
  date: string;
  categorie: string;
  montant: number;
  statut: string;
  description: string;
}

function kpi(label: string, value: string | number): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

function salarieOptions(salaries: Salarie[]): { value: string; label: string }[] {
  const actifs = salaries.filter((s) => s.statut !== 'sortie');
  if (actifs.length === 0) return [{ value: '', label: 'Aucun salarié actif' }];
  return actifs.map((s) => ({ value: s.id, label: `${s.prenom} ${s.nom}` }));
}

function salarieLabel(salaries: Salarie[], id: string): string {
  const s = salaries.find((x) => x.id === id);
  return s ? `${s.prenom} ${s.nom}` : '—';
}

function statutPill(statut: string): string {
  if (statut === 'Approuvée' || statut === 'Remboursée')
    return `<span class="pill success">${esc(statut)}</span>`;
  if (statut === 'Rejetée') return '<span class="pill danger">Rejetée</span>';
  return '<span class="pill warning">Soumise</span>';
}

function workflowActions(f: NoteFraisLocal, canWorkflow: boolean): string {
  if (!canWorkflow) return '';
  const parts: string[] = [];
  if (f.statut === 'Soumise') {
    parts.push(
      `<button class="btn btn-sm btn-primary" data-action="approve" data-id="${esc(f.id)}">Approuver</button>`,
      `<button class="btn btn-sm btn-danger" data-action="reject" data-id="${esc(f.id)}">Rejeter</button>`,
    );
  }
  if (f.statut === 'Approuvée') {
    parts.push(
      `<button class="btn btn-sm btn-secondary" data-action="reimburse" data-id="${esc(f.id)}">Remboursée</button>`,
    );
  }
  return parts.join(' ');
}

export const fraisController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission) || canMutateComptaRecords(shell.permission);
  const canWorkflow = canMutate;
  const salaries = await ctx.salaries.list();
  let items = await loadRegister<NoteFraisLocal>(tid, 'rh.notes_frais');

  const render = (): void => {
    const enAttente = items.filter((f) => f.statut === 'Soumise');
    const aRembourser = items
      .filter((f) => f.statut === 'Approuvée')
      .reduce((s, f) => s + (f.montant || 0), 0);
    const grid = section.querySelector('#frais-kpi-grid');
    if (grid) {
      grid.innerHTML =
        kpi('Notes de frais', items.length) +
        kpi('À traiter', enAttente.length) +
        kpi('À rembourser', fmtFCFA(aRembourser));
    }
    const tbody = section.querySelector('#frais-table tbody');
    const empty = section.querySelector<HTMLElement>('#frais-empty');
    if (empty) empty.style.display = items.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      items.length === 0
        ? ''
        : items
            .map(
              (f) => `<tr>
          <td><strong>${esc(salarieLabel(salaries, f.salarie_id))}</strong></td>
          <td>${esc(fmtDate(f.date))}</td>
          <td>${esc(f.categorie)}</td>
          <td style="font-size:12px;color:var(--gris);">${esc(f.description || '—')}</td>
          <td class="num">${esc(fmtFCFA(f.montant))}</td>
          <td>${statutPill(f.statut)}</td>
          <td class="num actions">${workflowActions(f, canWorkflow)}${canMutate ? crudActions(f.id) : ''}</td>
        </tr>`,
            )
            .join('');
  };
  render();

  const fields = (): FieldDef[] => [
    {
      kind: 'select',
      name: 'salarie_id',
      label: 'Salarié',
      options: salarieOptions(salaries),
      required: true,
    },
    { kind: 'date', name: 'date', label: 'Date', required: true },
    {
      kind: 'select',
      name: 'categorie',
      label: 'Catégorie',
      options: [
        { value: 'Transport', label: 'Transport' },
        { value: 'Mission / Déplacement', label: 'Mission / Déplacement' },
        { value: 'Hébergement', label: 'Hébergement' },
        { value: 'Restauration', label: 'Restauration' },
        { value: 'Carburant', label: 'Carburant' },
        { value: 'Fournitures', label: 'Fournitures' },
        { value: 'Autre', label: 'Autre' },
      ],
    },
    { kind: 'number', name: 'montant', label: 'Montant (FCFA)', min: 0, required: true },
    {
      kind: 'select',
      name: 'statut',
      label: 'Statut',
      options: [
        { value: 'Soumise', label: 'Soumise' },
        { value: 'Approuvée', label: 'Approuvée' },
        { value: 'Rejetée', label: 'Rejetée' },
        { value: 'Remboursée', label: 'Remboursée' },
      ],
    },
    { kind: 'text', name: 'description', label: 'Description' },
  ];

  const openEditor = (existing?: NoteFraisLocal): void => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la note de frais' : 'Nouvelle note de frais',
      fields: fields(),
      values: existing ?? {
        date: new Date().toISOString().slice(0, 10),
        statut: 'Soumise',
        categorie: 'Transport',
      },
      onSubmit: async (data) => {
        const salarieId = String(data['salarie_id'] ?? '');
        if (!salarieId) {
          ToastService.error('Sélectionnez un salarié');
          return;
        }
        const row: NoteFraisLocal = {
          id: existing?.id ?? uuid('nf'),
          salarie_id: salarieId,
          date: String(data['date'] ?? ''),
          categorie: String(data['categorie'] ?? 'Transport'),
          montant: Number(data['montant'] ?? 0),
          statut: String(data['statut'] ?? 'Soumise'),
          description: String(data['description'] ?? ''),
        };
        items = upsertById(items, row);
        await saveRegister(tid, 'rh.notes_frais', items);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#frais-table tbody'),
    section,
    (id) => openEditor(items.find((x) => x.id === id)),
    async (id) => {
      items = removeById(items, id);
      await saveRegister(tid, 'rh.notes_frais', items);
      render();
    },
    canMutate,
  );

  onExclusive(section.querySelector('#frais-table tbody'), 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!btn?.dataset.id || !canWorkflow) return;
    const id = btn.dataset.id;
    const action = btn.dataset.action;
    const statutMap: Record<string, string> = {
      approve: 'Approuvée',
      reject: 'Rejetée',
      reimburse: 'Remboursée',
    };
    const next = statutMap[action ?? ''];
    if (!next) return;
    const row = items.find((x) => x.id === id);
    if (!row) return;
    row.statut = next;
    void saveRegister(tid, 'rh.notes_frais', items).then(() => {
      ToastService.success('Statut mis à jour');
      render();
    });
  });

  const w = window as unknown as Record<string, unknown>;
  w['openFraisModal'] = () => openEditor();

  const btn = section.querySelector<HTMLElement>('#btn-new-frais');
  if (btn) btn.style.display = canMutate ? '' : 'none';
};
